import { Injectable, NgZone, inject } from '@angular/core';
import { SchedulerAction, SchedulerLike, Subscription, asyncScheduler, queueScheduler } from 'rxjs';

/**
 * In-house replacement for `@angular/fire`'s private `ɵAngularFireSchedulers`.
 *
 * AngularFire only exposes its zone-aware schedulers through a `ɵ`-prefixed (private/internal)
 * symbol that can change or disappear across `@angular/fire` major versions. This file vendors the
 * exact same behaviour so the Firebase Cloud Messaging service no longer depends on a private API.
 *
 * It mirrors `@angular/fire/src/zones.ts` 1:1: `outsideAngular` schedules work in the zone captured
 * via `NgZone.runOutsideAngular`, `insideAngular` schedules inside the Angular zone (and so triggers
 * change detection). Zone access is resilient to a zoneless runtime (`Zone` global absent).
 */
function currentZone(): { runGuarded: (fn: () => void) => void } | undefined {
  const zoneRef = (globalThis as { Zone?: { current: { runGuarded: (fn: () => void) => void } } }).Zone;
  return zoneRef ? zoneRef.current : undefined;
}

export class FvZoneScheduler implements SchedulerLike {
  constructor(
    private readonly zone: { runGuarded: (fn: () => void) => void } | undefined,
    private readonly delegate: SchedulerLike = queueScheduler
  ) {}

  now(): number {
    return this.delegate.now();
  }

  schedule(
    work: (this: SchedulerAction<unknown>, state?: unknown) => void,
    delay?: number,
    state?: unknown
  ): Subscription {
    const targetZone = this.zone;
    const workInZone = function (this: SchedulerAction<unknown>, innerState: unknown): void {
      if (targetZone) {
        targetZone.runGuarded(() => {
          work.apply(this, [innerState]);
        });
      } else {
        work.apply(this, [innerState]);
      }
    };
    return this.delegate.schedule(workInZone, delay, state);
  }
}

@Injectable({
  providedIn: 'root',
})
export class FvFirebaseZoneSchedulers {
  readonly outsideAngular: FvZoneScheduler;
  readonly insideAngular: FvZoneScheduler;

  constructor() {
    const ngZone = inject(NgZone);
    this.outsideAngular = ngZone.runOutsideAngular(() => new FvZoneScheduler(currentZone()));
    this.insideAngular = ngZone.run(() => new FvZoneScheduler(currentZone(), asyncScheduler));
  }
}
